import React, { useState } from "react";
import styled from "styled-components";
import { DropdownSingle, colors2, UI_BODY_SM_DF, DateRangePicker } from "oolib";
import { ButtonPrimary, ButtonSecondary } from "oolib/dist/v2/components/Buttons";
import {
  InterfaceContainer,
  InterfaceHeader,
  InterfaceDescription,
  InterfaceActions,
  HelpText,
  ConnectedBanner,
  ConnectedMeta,
  StaleBanner,
  ErrorBanner,
  EmptyBanner,
} from "./BaseIntegrationInterface";
import { useOAuthConnection } from "../../hooks/useOAuthConnection";
import { IntegrationDisconnectConfirmModal } from "@/_self_managed/SM_Integrations/IntegrationDisconnectConfirmModal";

// ============================================
// TYPEFORM ICON
// ============================================

export const TypeformIcon = ({ size = 32 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 32 32"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="32" height="32" rx="6" fill="#000000" />
    <path fill="#FFFFFF" d="M9 9.5h14v3H17.5v10h-3v-10H9z" />
  </svg>
);

const DateRangeRow = styled.div`
  margin-top: 16px;
`;

const DateRangeHint = styled(UI_BODY_SM_DF)`
  color: ${colors2.grey60};
  margin-top: 8px;
`;

// End date defaults to today (user can still change or clear it).
const todayIso = () => new Date().toISOString().slice(0, 10);

export const TypeformInterface = ({
  adapterConfig,
  onSubmit,
  isLoading,
  submitError,
  onClearSubmitError,
}) => {
  const {
    connection,
    connectionLoading,
    connectError,
    preflightData,
    preflightError,
    isPreflightLoading,
    handleConnect,
    isConnecting,
    handleDisconnect,
  } = useOAuthConnection({ providerId: "typeform", providerName: "Typeform" });

  const [selectedForm, setSelectedForm] = useState(null);
  const [dateRange, setDateRange] = useState([todayIso(), todayIso()]);
  const [startDate, endDate] = dateRange;

  const submitErrorMessage =
    submitError && submitError.sourceId === "typeform" ? submitError.message : null;
  const isEmptyResult =
    submitErrorMessage && /no\s+(typeform\s+)?(responses|data)\s+(found|to)/i.test(submitErrorMessage);

  const forms = preflightData?.forms || [];
  const canSubmit = Boolean(selectedForm?.value);

  const onDisconnect = async () => {
    await handleDisconnect();
    setSelectedForm(null);
  };

  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const confirmDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      await onDisconnect();
    } finally {
      setIsDisconnecting(false);
      setShowDisconnectConfirm(false);
    }
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    onClearSubmitError && onClearSubmitError();
    onSubmit({
      adapterOptions: {
        formId: selectedForm.value,
        ...(startDate ? { startDate } : {}),
        ...(endDate ? { endDate } : {}),
      },
    });
  };

  if (connectionLoading) {
    return (
      <InterfaceContainer>
        <InterfaceHeader>Connect to Typeform</InterfaceHeader>
        <InterfaceDescription>Checking connection status…</InterfaceDescription>
      </InterfaceContainer>
    );
  }

  if (!connection?.connected) {
    return (
      <InterfaceContainer>
        <InterfaceHeader>Connect to Typeform</InterfaceHeader>
        <InterfaceDescription>
          Import survey responses from your Typeform account. You'll be
          redirected to Typeform to authorize access, then brought back here to
          pick a form and import its responses.
        </InterfaceDescription>
        {connectError && <ErrorBanner>{connectError}</ErrorBanner>}
        <InterfaceActions>
          <ButtonPrimary onClick={handleConnect} isLoading={isConnecting} disabled={isConnecting}>Connect Typeform</ButtonPrimary>
        </InterfaceActions>
        <HelpText>
          Dots uses OAuth to access your Typeform account. We request read
          access to your forms and responses — your team never sees the raw API
          token.
        </HelpText>
      </InterfaceContainer>
    );
  }

  if (connection.status === "stale") {
    return (
      <InterfaceContainer>
        <InterfaceHeader>Reconnect Typeform</InterfaceHeader>
        <StaleBanner>
          <ConnectedMeta>
            <strong>⚠ Connection needs to be renewed</strong>
            <span>
              {connection.providerAccountName
                ? `Your Typeform connection to ${connection.providerAccountName} expired or was revoked.`
                : "Your Typeform connection expired or was revoked."}
            </span>
          </ConnectedMeta>
          <ButtonPrimary onClick={handleConnect} isLoading={isConnecting} disabled={isConnecting}>Reconnect Typeform</ButtonPrimary>
        </StaleBanner>
        {connectError && <ErrorBanner>{connectError}</ErrorBanner>}
      </InterfaceContainer>
    );
  }

  return (
    <InterfaceContainer>
      <InterfaceHeader>Import from Typeform</InterfaceHeader>
      <InterfaceDescription>
        Each Typeform response becomes one row. Question titles map to columns;
        hidden fields and submission metadata are included.
      </InterfaceDescription>

      <ConnectedBanner>
        <ConnectedMeta>
          <strong>
            ✓ Connected{connection.providerAccountName ? ` as ${connection.providerAccountName}` : ""}
          </strong>
          {connection.adminEmail && (
            <span style={{ color: colors2.grey60 }}>{connection.adminEmail}</span>
          )}
          {connection.authorizedBy?.name && (
            <span style={{ color: colors2.grey60 }}>
              Authorized by: {connection.authorizedBy.name}
            </span>
          )}
        </ConnectedMeta>
        <ButtonSecondary onClick={() => setShowDisconnectConfirm(true)} disabled={isLoading}>
          Disconnect
        </ButtonSecondary>
      </ConnectedBanner>

      {showDisconnectConfirm && (
        <IntegrationDisconnectConfirmModal
          name="Typeform"
          onConfirm={confirmDisconnect}
          onClose={() => setShowDisconnectConfirm(false)}
          isLoading={isDisconnecting}
        />
      )}

      {connectError && <ErrorBanner>{connectError}</ErrorBanner>}
      {preflightError && <ErrorBanner>{preflightError}</ErrorBanner>}
      {submitErrorMessage && isEmptyResult && <EmptyBanner>{submitErrorMessage}</EmptyBanner>}
      {submitErrorMessage && !isEmptyResult && <ErrorBanner>{submitErrorMessage}</ErrorBanner>}

      <div style={{ marginTop: 16 }}>
        <DropdownSingle
          label={adapterConfig?.preflightConfig?.selectLabel || "Select Form"}
          placeholder={
            isPreflightLoading
              ? "Loading forms..."
              : adapterConfig?.preflightConfig?.selectPlaceholder || "Choose a form to import..."
          }
          options={forms.map((f) => ({
            display: f.title || "(untitled form)",
            value: f.id,
            _formInfo: f,
          }))}
          value={selectedForm}
          onChange={(k, v) => setSelectedForm(v)}
          readOnly={isPreflightLoading || isLoading}
          lightboxHeight="small"
          popOutOfOverflowHiddenParent={true}
        />
      </div>

      <DateRangeRow>
        <DateRangePicker
          id="typeform-date-range"
          label="Date Range (optional)"
          value={dateRange}
          onChange={(k, v) => setDateRange(v)}
          readOnly={isLoading}
        />
      </DateRangeRow>

      <DateRangeHint>
        Leave dates blank to import every response in the selected form. Set a
        range if you want to limit by submission time.
      </DateRangeHint>

      <InterfaceActions>
        <ButtonPrimary
          onClick={handleSubmit}
          disabled={!canSubmit || isPreflightLoading || isLoading}
          isLoading={isLoading}
        >
          {adapterConfig?.preflightConfig?.submitLabel || "Import Responses"}
        </ButtonPrimary>
      </InterfaceActions>
    </InterfaceContainer>
  );
};

export const typeformIntegrationMeta = {
  id: "typeform",
  name: "Typeform",
  description: "Import survey responses from Typeform",
};

export default TypeformInterface;
